import { useMemo } from 'react';

import api from '@mint-lib/api';
import { useQuery } from '@mint-lib/query-client';

import { isDeprecatedPlatform } from '../constants/deprecatedPlatforms.js';
import { QueryKey } from '../constants/queryKeys.js';
import {
  GetPlatformsResponse,
  Platform,
  PlatformsResponse,
} from '../types/integrations.js';

const getPlatformsIntegrations = async (): Promise<PlatformsResponse> => {
  const response = await api.get('/api/bff/v2/platforms/');

  return GetPlatformsResponse.parse(response.data);
};

function useGetPlatformIntegrations() {
  const { data, isLoading, isError } = useQuery({
    queryKey: [QueryKey.PLATFORM_INTEGRATIONS],
    queryFn: () => getPlatformsIntegrations(),
    staleTime: Infinity,
  });

  const integratedPlatforms = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.filter((p) => {
      if (isDeprecatedPlatform(p.platform)) {
        return false;
      }
      return p.isIntegrated && !p.isDeleted;
    });
  }, [data]);

  const platformByName = useMemo(() => {
    if (!data) {
      return {};
    }
    return data.reduce(
      (acc: { [key: Platform['platform']]: Platform }, item) => {
        return {
          ...acc,
          [item.platform]: item,
        };
      },
      {},
    );
  }, [data]);

  return {
    platforms: data,
    isLoading,
    isError,
    integratedPlatforms,
    platformByName,
  };
}

export default useGetPlatformIntegrations;
